import { CloudRain, Umbrella, Droplets } from "lucide-react";
import GullyFusion from "@/components/GullyFusion";

export default function RainfallPanel({ rainfall, currentPrecip, forecastHour = 0, active, runoff }) {
  const hourly = rainfall?.hourly_mm || [];
  const start = active ? Math.max(0, Math.min(forecastHour, hourly.length - 24)) : 0;
  const window = hourly.slice(start, start + 24);
  const maxBar = Math.max(1, ...window);
  const next24 = active
    ? Math.round(window.reduce((a, b) => a + (b || 0), 0) * 10) / 10
    : rainfall?.next_24h_mm ?? 0;
  const peak = rainfall?.peak_rate_mm ?? 0;
  const now = currentPrecip != null ? currentPrecip : (hourly[start] ?? 0);

  const band = next24 > 50 ? { c: "#FF3B30", l: "TORRENTIAL" }
    : next24 > 20 ? { c: "#FF9F0A", l: "HEAVY" }
    : next24 > 5 ? { c: "#FFD60A", l: "MODERATE" }
    : { c: "#30D158", l: "LIGHT" };

  return (
    <div data-testid="rainfall-panel" className="glass rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CloudRain className="w-4 h-4 text-cyan" />
          <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">
            Rainfall Forecast
          </h2>
        </div>
        <span className="font-mono-t text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ color: band.c, background: `${band.c}1a` }}>
          {band.l}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat icon={Droplets} label="Now" value={now} unit="mm/h" testid="rain-now" />
        <Stat icon={Umbrella} label="Next 24h" value={next24} unit="mm" testid="rain-24h" accent={band.c} />
        <Stat icon={CloudRain} label="Peak" value={peak} unit="mm/h" sub={`+${rainfall?.peak_hour ?? 0}h`} testid="rain-peak" />
      </div>

      <div>
        <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-500 mb-1">
          Next 24h hourly
        </div>
        <div className="flex items-end gap-[2px] h-10" data-testid="rain-sparkline">
          {window.map((v, i) => (
            <div key={i} className="flex-1 rounded-t-sm transition-all duration-300"
                 style={{ height: `${Math.max(3, ((v || 0) / maxBar) * 100)}%`,
                          background: (v || 0) > 0 ? band.c : "#1e293b", opacity: (v || 0) > 0 ? 0.9 : 0.4 }}
                 title={`+${start + i}h: ${v || 0}mm`} />
          ))}
        </div>
      </div>

      <GullyFusion runoff={runoff} peak={rainfall?.gully_peak} />
    </div>
  );
}

function Stat({ icon: Icon, label, value, unit, sub, accent = "#f8fafc", testid }) {
  return (
    <div data-testid={testid} className="bg-slate-900/60 border border-border rounded-md px-2.5 py-2">
      <div className="flex items-center gap-1 font-mono-t text-[9px] uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-heading font-bold text-lg leading-tight" style={{ color: accent }}>
        {value}<span className="text-xs text-slate-400 ml-0.5">{unit}</span>
      </div>
      {sub && <div className="font-mono-t text-[9px] text-slate-500">{sub}</div>}
    </div>
  );
}
